import * as React from 'react';
import { useMemo, useState } from 'react';
import AreaShell from '../../chrome/AreaShell';
import { Segmented, TCard, TFoot, Toolbar, TRow } from '../../components/table';
import { ConfirmDialog, Dialog, Field, Select, TextInput } from '../../components/forms';
import { ListState, useList, useToast } from '../../components/roster';
import useAccounts from '@/src/Accounts/components/hooks/useAccounts';
import useCreateAccount from '@/src/Accounts/components/hooks/useCreateAccount';
import useEditAccount from '@/src/Accounts/components/hooks/useEditAccount';
import useAuth from '@/src/Authentication/components/hooks/useAuth';
import useAuthenticatedApi from '@/src/Authentication/components/hooks/useAuthenticatedApi';
import useUserOrg from '@/src/Authentication/components/hooks/useUserOrg';

/*
 * Who can sign in to this organization. Same requests as the v2 page;
 * role and password changes each get their own dialog. The server
 * enforces admin on every accounts route.
 */

interface AccountRow {
    id: number;
    username: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    accountRoleId?: number;
}

const ADMIN = 0;
const GLOBAL = 69;

const ROLE_LABELS: Record<number, string> = {
    0: 'Admin',
    1: 'User',
    2: 'Client',
    69: 'Global admin',
};

const ROLE_HELP: Record<number, string> = {
    0: 'Full control of this organization: adding and deleting accounts, changing roles and passwords, white-label, and every organization setting.',
    1: 'Can use the dashboard to run events and galleries, but can’t open organization settings.',
    2: 'Can’t open this dashboard.',
    69: 'Smilebooth staff access across every organization.',
};

type RoleFilter = 'all' | 'admin' | 'user' | 'client';

const displayName = (a: AccountRow) =>
    [a.firstName, a.lastName].filter(Boolean).join(' ') || a.username;
const roleLabel = (role?: number) => ROLE_LABELS[role ?? -1] ?? '—';
const isAdminRole = (role?: number) => role === ADMIN || role === GLOBAL;

const BLANK = {
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    role: 1,
};

export default function OrgAccountsV3() {
    const { getAccounts } = useAccounts();
    const { createAccount } = useCreateAccount();
    const { editAccount, setPassword } = useEditAccount();
    const { auth } = useAuth() as any;
    const { orgId } = useUserOrg();
    const authenticatedApi = useAuthenticatedApi();
    const toast = useToast();

    const list = useList<AccountRow>(() => getAccounts(), [orgId]);
    const accounts = useMemo(() => list.rows ?? [], [list.rows]);

    const [query, setQuery] = useState('');
    const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
    const q = query.trim().toLowerCase();
    const visible = useMemo(
        () =>
            accounts
                .filter(
                    (a) =>
                        !q ||
                        `${a.username} ${a.email ?? ''} ${displayName(a)}`.toLowerCase().includes(q),
                )
                .filter((a) =>
                    roleFilter === 'all'
                        ? true
                        : roleFilter === 'admin'
                          ? isAdminRole(a.accountRoleId)
                          : roleFilter === 'user'
                            ? a.accountRoleId === 1
                            : a.accountRoleId === 2,
                )
                .sort((a, b) => displayName(a).localeCompare(displayName(b))),
        [accounts, q, roleFilter],
    );
    const isSelf = (a: AccountRow) => !!auth?.username && a.username === auth.username;

    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    /* ---- create ---- */
    const [creating, setCreating] = useState(false);
    const [draft, setDraft] = useState(BLANK);
    const openCreate = () => {
        setDraft(BLANK);
        setError(null);
        setCreating(true);
    };
    const canCreate =
        !!draft.username.trim() &&
        !!draft.email.trim() &&
        !!draft.firstName.trim() &&
        !!draft.lastName.trim() &&
        draft.password.length > 0;
    const submitCreate = async () => {
        if (!canCreate || busy) return;
        setBusy(true);
        setError(null);
        const response = await createAccount({
            username: draft.username.trim(),
            email: draft.email.trim(),
            firstName: draft.firstName.trim(),
            lastName: draft.lastName.trim(),
            password: draft.password,
            role: draft.role,
            orgId,
        });
        setBusy(false);
        if (response?.status === 200 || response?.data?.id) {
            setCreating(false);
            toast.show(`Added ${draft.username.trim()}.`);
            list.reload();
        } else {
            setError(response?.message ?? 'Couldn’t create the account.');
        }
    };

    /* ---- edit details (role is sent unchanged; it has its own dialog) ---- */
    const [editing, setEditing] = useState<AccountRow | null>(null);
    const [details, setDetails] = useState({ username: '', email: '', firstName: '', lastName: '' });
    const openEdit = (a: AccountRow) => {
        setDetails({
            username: a.username ?? '',
            email: a.email ?? '',
            firstName: a.firstName ?? '',
            lastName: a.lastName ?? '',
        });
        setError(null);
        setEditing(a);
    };
    const submitEdit = async () => {
        if (!editing || busy || !details.username.trim()) return;
        setBusy(true);
        setError(null);
        const ok = await editAccount({
            id: editing.id,
            username: details.username.trim(),
            email: details.email.trim(),
            firstName: details.firstName.trim(),
            lastName: details.lastName.trim(),
            role: editing.accountRoleId ?? 1,
        }).catch(() => false);
        setBusy(false);
        if (ok) {
            setEditing(null);
            toast.show('Saved.');
            list.reload();
        } else {
            setError('Couldn’t save. Try again.');
        }
    };

    /* ---- change role ---- */
    const [roleFor, setRoleFor] = useState<AccountRow | null>(null);
    const [nextRole, setNextRole] = useState(1);
    const openRole = (a: AccountRow) => {
        setNextRole(a.accountRoleId ?? 1);
        setError(null);
        setRoleFor(a);
    };
    const roleChanged = !!roleFor && nextRole !== (roleFor.accountRoleId ?? 1);
    const submitRole = async () => {
        if (!roleFor || busy || !roleChanged) return;
        setBusy(true);
        setError(null);
        const ok = await editAccount({
            id: roleFor.id,
            username: (roleFor.username ?? '').trim(),
            email: (roleFor.email ?? '').trim(),
            firstName: (roleFor.firstName ?? '').trim(),
            lastName: (roleFor.lastName ?? '').trim(),
            role: nextRole,
        }).catch(() => false);
        setBusy(false);
        if (ok) {
            toast.show(`${displayName(roleFor)} is now ${roleLabel(nextRole)}.`);
            setRoleFor(null);
            list.reload();
        } else {
            setError('Couldn’t change the role. Try again.');
        }
    };

    /* ---- set password ---- */
    const [pwFor, setPwFor] = useState<AccountRow | null>(null);
    const [pw, setPw] = useState({ password: '', confirm: '' });
    const openPassword = (a: AccountRow) => {
        setPw({ password: '', confirm: '' });
        setError(null);
        setPwFor(a);
    };
    const mismatch = pw.confirm.length > 0 && pw.password !== pw.confirm;
    const submitPassword = async () => {
        if (!pwFor || busy) return;
        if (pw.password.length === 0 || pw.password !== pw.confirm) {
            setError('Passwords must match.');
            return;
        }
        setBusy(true);
        setError(null);
        const ok = await setPassword(pwFor.id, pw.password).catch(() => false);
        setBusy(false);
        if (ok) {
            toast.show(`Password updated for ${pwFor.username}.`);
            setPwFor(null);
            setPw({ password: '', confirm: '' });
        } else {
            setError('Couldn’t update the password.');
        }
    };

    /* ---- delete ---- */
    const [deleting, setDeleting] = useState<AccountRow | null>(null);
    const remove = async (a: AccountRow) => {
        const prev = list.rows;
        list.setRows((rows) => (rows ?? []).filter((r) => r.id !== a.id));
        const ok = await authenticatedApi
            .delete(`/accounts/${a.id}`)
            .then(() => true)
            .catch(() => false);
        if (ok) toast.show(`Deleted ${a.username}.`);
        else {
            list.setRows(prev);
            toast.show('Couldn’t delete the account.');
        }
    };

    const closeAll = () => {
        if (busy) return;
        setCreating(false);
        setEditing(null);
        setRoleFor(null);
        setPwFor(null);
    };

    const roleOptions = (current?: number) => (
        <>
            <option value={0}>Admin</option>
            <option value={1}>User</option>
            <option value={2}>Client</option>
            {current === GLOBAL && <option value={GLOBAL}>Global admin</option>}
        </>
    );

    const filtered = q || roleFilter !== 'all';

    return (
        <AreaShell
            area="org"
            tab="accounts"
            sub={list.rows ? `${accounts.length} ${accounts.length === 1 ? 'account' : 'accounts'} · who can sign in` : 'Who can sign in to this organization'}
            actions={
                <button className="tb-new" onClick={openCreate}>
                    Add account
                </button>
            }
        >
            <Toolbar
                searchValue={query}
                onSearch={setQuery}
                searchPlaceholder="Search name, username, or email"
                filterCount={0}
                inline={
                    <Segmented<RoleFilter>
                        label="Role"
                        options={[
                            { value: 'all', label: 'All' },
                            { value: 'admin', label: 'Admins' },
                            { value: 'user', label: 'Users' },
                            { value: 'client', label: 'Clients' },
                        ]}
                        value={roleFilter}
                        onChange={setRoleFilter}
                    />
                }
            />
            <ListState
                rows={list.rows}
                failed={list.failed}
                what="accounts"
                reload={list.reload}
                empty={
                    visible.length === 0
                        ? accounts.length === 0
                            ? 'No accounts yet.'
                            : q
                              ? `Nothing matches “${query}”.`
                              : 'No accounts with this role.'
                        : undefined
                }
                emptyAction={accounts.length === 0 ? 'Add account' : undefined}
                onEmptyAction={openCreate}
            >
                <TCard>
                    {visible.map((a, i) => {
                        const self = isSelf(a);
                        return (
                            <TRow
                                key={a.id}
                                index={i}
                                name={displayName(a)}
                                badge={self ? 'You' : undefined}
                                meta={[a.username, a.email].filter(Boolean).join(' · ')}
                                metaDate={roleLabel(a.accountRoleId)}
                                date={a.email || a.username}
                                status={roleLabel(a.accountRoleId)}
                                onOpen={() => openEdit(a)}
                                menuItems={[
                                    { label: 'Edit details', onClick: () => openEdit(a) },
                                    { label: 'Change role…', onClick: () => openRole(a) },
                                    { label: 'Set password…', onClick: () => openPassword(a) },
                                    ...(self ? [] : [{ label: 'Delete…', onClick: () => setDeleting(a) }]),
                                ]}
                            />
                        );
                    })}
                </TCard>
            </ListState>
            {list.rows && !list.failed && (
                <TFoot>
                    {filtered
                        ? `${visible.length} of ${accounts.length} · filtered`
                        : `${accounts.length} ${accounts.length === 1 ? 'account' : 'accounts'}`}
                </TFoot>
            )}

            <Dialog
                open={creating}
                title="Add account"
                onClose={closeAll}
                onSubmit={submitCreate}
                footer={
                    <>
                        <button type="button" className="tb-btn" onClick={closeAll} disabled={busy}>
                            Cancel
                        </button>
                        <button type="submit" className="tb-new" disabled={!canCreate || busy}>
                            {busy ? 'Creating…' : 'Create account'}
                        </button>
                    </>
                }
            >
                <div className="two">
                    <Field label="First name">
                        <TextInput autoFocus autoComplete="off" value={draft.firstName} onChange={(e) => setDraft({ ...draft, firstName: e.target.value })} />
                    </Field>
                    <Field label="Last name">
                        <TextInput value={draft.lastName} onChange={(e) => setDraft({ ...draft, lastName: e.target.value })} />
                    </Field>
                </div>
                <Field label="Username">
                    <TextInput autoCapitalize="none" value={draft.username} onChange={(e) => setDraft({ ...draft, username: e.target.value })} />
                </Field>
                <Field label="Email">
                    <TextInput type="email" inputMode="email" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} />
                </Field>
                <Field label="Password" help="Share it with them privately. They can change it after signing in.">
                    <TextInput type="password" autoComplete="new-password" value={draft.password} onChange={(e) => setDraft({ ...draft, password: e.target.value })} />
                </Field>
                <Field label="Role" help={ROLE_HELP[draft.role]} error={error ?? undefined}>
                    <Select value={draft.role} onChange={(e) => setDraft({ ...draft, role: +e.target.value })}>
                        {roleOptions()}
                    </Select>
                </Field>
            </Dialog>

            <Dialog
                open={editing !== null}
                title={editing ? `Edit ${editing.username}` : ''}
                onClose={closeAll}
                onSubmit={submitEdit}
                footer={
                    <>
                        <button type="button" className="tb-btn" onClick={closeAll} disabled={busy}>
                            Cancel
                        </button>
                        <button type="submit" className="tb-new" disabled={busy || !details.username.trim()}>
                            {busy ? 'Saving…' : 'Save'}
                        </button>
                    </>
                }
            >
                <div className="two">
                    <Field label="First name">
                        <TextInput autoFocus value={details.firstName} onChange={(e) => setDetails({ ...details, firstName: e.target.value })} />
                    </Field>
                    <Field label="Last name">
                        <TextInput value={details.lastName} onChange={(e) => setDetails({ ...details, lastName: e.target.value })} />
                    </Field>
                </div>
                <Field label="Username" error={!details.username.trim() ? 'Username is required.' : undefined}>
                    <TextInput autoCapitalize="none" value={details.username} onChange={(e) => setDetails({ ...details, username: e.target.value })} />
                </Field>
                <Field
                    label="Email"
                    help={editing ? `Role: ${roleLabel(editing.accountRoleId)}. Change it from the row menu.` : undefined}
                    error={error ?? undefined}
                >
                    <TextInput type="email" inputMode="email" value={details.email} onChange={(e) => setDetails({ ...details, email: e.target.value })} />
                </Field>
            </Dialog>

            <Dialog
                open={roleFor !== null}
                title={roleFor ? `Change role for ${displayName(roleFor)}` : ''}
                onClose={closeAll}
                onSubmit={submitRole}
                footer={
                    <>
                        <button type="button" className="tb-btn" onClick={closeAll} disabled={busy}>
                            Cancel
                        </button>
                        <button type="submit" className="tb-new" disabled={busy || !roleChanged}>
                            {busy ? 'Changing…' : roleChanged ? `Make ${roleLabel(nextRole)}` : 'Change role'}
                        </button>
                    </>
                }
            >
                {roleFor && (
                    <>
                        <p className="pg-sub" style={{ margin: '0 0 12px' }}>
                            {roleFor.username} is currently {roleLabel(roleFor.accountRoleId)}. The change applies the next time they load the dashboard.
                        </p>
                        <Field label="New role" help={ROLE_HELP[nextRole]} error={error ?? undefined}>
                            <Select value={nextRole} onChange={(e) => setNextRole(+e.target.value)}>
                                {roleOptions(roleFor.accountRoleId)}
                            </Select>
                        </Field>
                        {roleChanged && nextRole === ADMIN && (
                            <p className="pg-sub" style={{ margin: '12px 0 0' }}>
                                <strong>Admins can add and delete accounts, change anyone’s role or password, and edit white-label and every organization setting.</strong>
                            </p>
                        )}
                        {roleChanged && isSelf(roleFor) && isAdminRole(roleFor.accountRoleId) && !isAdminRole(nextRole) && (
                            <p className="pg-sub" style={{ margin: '12px 0 0' }}>
                                <strong>This is your own account. You’ll lose access to organization settings, including this page.</strong>
                            </p>
                        )}
                    </>
                )}
            </Dialog>

            <Dialog
                open={pwFor !== null}
                title={pwFor ? `Set password for ${pwFor.username}` : ''}
                onClose={closeAll}
                onSubmit={submitPassword}
                footer={
                    <>
                        <button type="button" className="tb-btn" onClick={closeAll} disabled={busy}>
                            Cancel
                        </button>
                        <button type="submit" className="tb-new" disabled={busy || pw.password.length === 0 || pw.password !== pw.confirm}>
                            {busy ? 'Saving…' : 'Set password'}
                        </button>
                    </>
                }
            >
                <p className="pg-sub" style={{ margin: '0 0 12px' }}>
                    This replaces their current password right away. Share the new one with them privately.
                </p>
                <Field label="New password">
                    <TextInput autoFocus type="password" autoComplete="new-password" value={pw.password} onChange={(e) => setPw({ ...pw, password: e.target.value })} />
                </Field>
                <Field label="Confirm new password" error={mismatch ? 'Passwords must match.' : error ?? undefined}>
                    <TextInput type="password" autoComplete="new-password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} />
                </Field>
            </Dialog>

            <ConfirmDialog
                open={deleting !== null}
                title={deleting ? `Delete ${deleting.username}?` : ''}
                body={`${deleting ? displayName(deleting) : 'They'} lose dashboard access immediately. This can’t be undone.`}
                actionLabel="Delete account"
                onConfirm={() => deleting && remove(deleting)}
                onClose={() => setDeleting(null)}
            />
            {toast.node}
        </AreaShell>
    );
}
